import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import {
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Alert,
  TablePagination,
  DialogTitle,
  Typography,
  DialogActions,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MetadataFilters from "../../../components/Admin/Metadata/MetadataFilters";
import MetadataTable from "../../../components/Admin/Metadata/MetadataTable";
import MetadataForm from "../../../components/Admin/Metadata/MetadataForm";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { customInstanceBpm } from "../../../api/axios-client";
import { useAuth } from "../../../auth";

const metadataSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
});

export const Route = createFileRoute("/__auth/admin/metadata")({
  component: MetadataComponent,
  validateSearch: (search) => metadataSearchSchema.parse(search),
});

function MetadataComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { currentTenant } = useAuth();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const effectiveTenant = currentTenant || "default-domain";

  const {
    data: metadatasData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "metadatas",
      effectiveTenant,
      search.page,
      search.size,
      search.q,
    ],
    queryFn: async () => {
      const page = (search.page || 1) - 1;
      const size = search.size || 10;
      const q = search.q || "";
      const res = await customInstanceBpm<any>(
        `/v1/${effectiveTenant}/metadatas?page=${page}&size=${size}&sort=id,asc&q=${q}`,
      );
      return res;
    },
    staleTime: 30000,
  });

  const rows = useMemo(() => metadatasData?.content || [], [metadatasData]);

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilter = (newFilters: { q?: string }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1,
      }),
    });
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const saveMutation = useMutation({
    mutationFn: async (values: any) => {
      const selectedTypeName: string = values.type?.name || "";

      const typesCache =
        queryClient.getQueryData<any[]>(["metadata-types", effectiveTenant]) ||
        [];
      const hasTypeInBackend = typesCache.some(
        (t: any) => t.name === selectedTypeName,
      );

      const FALLBACK_TYPE_MAP: Record<string, string> = {
        CNPJ: "Text",
        CPF: "Text",
        DocumentViewer: "Text",
        DynamicList: "Text",
        Etapa: "Typography",
      };

      let typeToSend = { ...values.type };

      const flatProps = { ...(values.properties || {}) };

      if (!hasTypeInBackend && selectedTypeName) {
        const fallbackName = FALLBACK_TYPE_MAP[selectedTypeName] || "Text";
        const fallbackType =
          typesCache.find((t: any) => t.name === fallbackName) || typesCache[0];

        if (fallbackType) {
          typeToSend = { id: fallbackType.id, name: fallbackType.name };
        }

        flatProps.realType = selectedTypeName;
      } else {
        delete flatProps.realType;
      }

      const legacyProps: any[] = [];

      Object.keys(flatProps).forEach((key) => {
        if (["onBlur_api_mapping", "dataSourceType"].includes(key)) return;

        const value = flatProps[key];
        if (value === undefined || value === null) return;

        let type =
          typeof value === "boolean"
            ? "boolean"
            : typeof value === "number"
              ? "number"
              : "text";

        const prop: any = {
          id: key,
          value: value,
          text: key,
          type: type,
        };

        if (key === "options") {
          prop.type = flatProps.dataSourceType || "options";
        }

        if (key === "onBlur_api") {
          prop.mapFields = flatProps.onBlur_api_mapping || [];
        }

        if (key === "waitVariablesOptions") {
          prop.type = "object";
        }

        if (key === "acceptedFiles") {
          prop.type = "object";
        }

        legacyProps.push(prop);
      });

      const payload = {
        ...values,
        type: typeToSend,
        properties: JSON.stringify(legacyProps),
      };

      if (selectedItem?.id) {
        return customInstanceBpm<any>(
          `/v1/${effectiveTenant}/metadatas/${selectedItem.id}`,
          {
            method: "PUT",
            body: JSON.stringify(payload),
            headers: { "Content-Type": "application/json" },
          },
        );
      }
      return customInstanceBpm<any>(`/v1/${effectiveTenant}/metadatas`, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["metadatas"] });
      showSnackbar(
        t(
          `metadata.list.${selectedItem?.id ? "updateSuccess" : "saveSuccess"}`,
        ),
      );
      setIsDialogOpen(false);
    },
    onError: () => showSnackbar(t("metadata.list.saveError"), "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return customInstanceBpm<any>(
        `/v1/${effectiveTenant}/metadatas/${id}`,
        {
          method: "DELETE",
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["metadatas"] });
      showSnackbar(t("metadata.list.deleteSuccess"));
    },
    onError: () => showSnackbar(t("metadata.list.deleteError"), "error"),
  });

  return (
    <ListLayout
      title={t("metadata.title")}
      subtitle={t("metadata.subtitle")}
      filters={
        <MetadataFilters
          onApply={handleFilter}
          initialValues={{ q: search.q }}
        />
      }
      isLoading={isLoading}
      isEmpty={!rows.length}
      error={error as any}
      actions={
        <IconButton
          color="primary"
          onClick={() => {
            setSelectedItem(null);
            setIsDialogOpen(true);
          }}
        >
          <AddIcon />
        </IconButton>
      }
    >
      <MetadataTable
        data={rows}
        isLoading={isLoading}
        onEdit={(item) => {
          setSelectedItem(item);
          setIsDialogOpen(true);
        }}
        onDelete={(item) => {
          if (
            window.confirm(
              t("metadata.list.deleteConfirm", { name: item.name }),
            )
          ) {
            deleteMutation.mutate(item.id);
          }
        }}
      />

      <TablePagination
        component="div"
        count={metadatasData?.totalElements || 0}
        page={(search.page || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          navigate({
            search: (prev) => ({ ...prev, size: newSize, page: 1 }),
            replace: true,
          });
        }}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t("common.of")} ${count}`
        }
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      />

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent>
          <MetadataForm
            initialValues={selectedItem || undefined}
            onSubmit={(vals) => saveMutation.mutate(vals)}
            onCancel={() => setIsDialogOpen(false)}
            isLoading={saveMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("metadata.list.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("metadata.list.deleteConfirm", { name: itemToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (itemToDelete?.id) {
                deleteMutation.mutate(itemToDelete.id);
              }
              setIsDeleteDialogOpen(false);
              setItemToDelete(null);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
